/**
 * Contexto de autenticación + watchlist (RFC-0020).
 *
 * Modelo aditivo: el mapa es público; iniciar sesión SUMA la watchlist
 * "en observación" (estrella ★ + notas) y, para admins, el panel de
 * usuarios. La cookie de sesión es httpOnly, así que el estado de auth
 * se descubre con `GET /v1/auth/me` al montar — no leemos tokens en JS.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, clearRequestCache } from './api';
import type { AuthUser, LegalStatus, WatchlistItem, UsageSummary } from './types';

interface AuthContextValue {
  user: AuthUser | null;
  /** Consumo del mes de la organización (RFC-0025); null sin organización. */
  usage: UsageSummary | null;
  /** true mientras corre el chequeo inicial de sesión. */
  loading: boolean;
  /** Términos vigentes (null = mecanismo apagado). */
  legal: LegalStatus | null;
  acceptTerms: (version: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  /** Enlace de invitación o restablecimiento (RFC-0026): fija la contraseña y entra. */
  completeToken: (token: string, newPassword: string) => Promise<'invite' | 'reset'>;
  refreshUser: () => Promise<void>;

  // Watchlist
  watchIds: Set<string>;
  watchItems: WatchlistItem[];
  isWatching: (propertyId: string) => boolean;
  toggleWatch: (propertyId: string, note?: string) => Promise<void>;
  setNote: (propertyId: string, note: string) => Promise<void>;
  refreshWatchlist: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [legal, setLegal] = useState<LegalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [watchItems, setWatchItems] = useState<WatchlistItem[]>([]);
  const [watchIds, setWatchIds] = useState<Set<string>>(new Set());

  const refreshWatchlist = useCallback(async () => {
    try {
      const r = await api.watchlistGet();
      setWatchItems(r.items);
      setWatchIds(new Set(r.items.map((i) => i.id)));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Sesión expirada a media visita: antes esto vaciaba la watchlist
        // pero DEJABA `user` seteado — la UI mostraba el avatar con las
        // estrellas mudas y sin pista de re-login. Reflejamos la realidad:
        // ya no hay sesión.
        setUser(null);
        setWatchItems([]);
        setWatchIds(new Set());
        return;
      }
      // Error de red transitorio: conservar el estado actual en vez de
      // vaciar la watchlist por un blip de conexión.
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.authMe();
      setUser(r.user);
      setUsage(r.usage ?? null);
      setLegal(r.legal ?? null);
      await refreshWatchlist();
    } catch (err) {
      // 401 = anónimo (esperado); cualquier otra cosa también deja anónimo.
      setUser(null);
      setUsage(null);
      setLegal(null);
      setWatchItems([]);
      setWatchIds(new Set());
      if (!(err instanceof ApiError)) {
        // Error de red real: lo dejamos pasar silencioso, el mapa es público.
      }
    }
  }, [refreshWatchlist]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await refreshUser();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api.authLogin(email, password);
    clearRequestCache();
    setUser(r.user);
    await refreshWatchlist();
    // Consumo del plan y términos pendientes: best-effort, un fallo aquí no
    // deshace el login (se recuperan al recargar).
    try {
      const me = await api.authMe();
      setUsage(me.usage ?? null);
      setLegal(me.legal ?? null);
    } catch { /* noop */ }
  }, [refreshWatchlist]);

  const logout = useCallback(async () => {
    try { await api.authLogout(); } finally {
      clearRequestCache();
      setUser(null);
      setUsage(null);
      setWatchItems([]);
      setWatchIds(new Set());
    }
  }, []);

  const changePassword = useCallback(async (current: string, next: string) => {
    await api.authChangePassword(current, next);
    // Tras cambiar, refrescamos el flag must_change_password.
    await refreshUser();
  }, [refreshUser]);

  const acceptTerms = useCallback(async (version: string) => {
    await api.authAcceptTerms(version);
    await refreshUser();
  }, [refreshUser]);

  const completeToken = useCallback(async (token: string, newPassword: string) => {
    const r = await api.authTokenComplete(token, newPassword);
    // Igual que login: lo cacheado era de un visitante anónimo.
    clearRequestCache();
    await refreshUser();
    return r.purpose;
  }, [refreshUser]);

  const isWatching = useCallback((propertyId: string) => watchIds.has(propertyId), [watchIds]);

  const toggleWatch = useCallback(async (propertyId: string, note?: string) => {
    if (!user) return;
    const currentlyWatching = watchIds.has(propertyId);
    // Optimista para que la estrella responda al instante.
    setWatchIds((prev) => {
      const nextSet = new Set(prev);
      if (currentlyWatching) nextSet.delete(propertyId);
      else nextSet.add(propertyId);
      return nextSet;
    });
    try {
      if (currentlyWatching) await api.watchlistRemove(propertyId);
      else await api.watchlistAdd(propertyId, note);
      await refreshWatchlist();
    } catch (err) {
      // Revertir SOLO el flip optimista (antes se llamaba refreshWatchlist,
      // que en un fallo de red vaciaba el set COMPLETO de estrellas).
      setWatchIds((prev) => {
        const nextSet = new Set(prev);
        if (currentlyWatching) nextSet.add(propertyId);
        else nextSet.delete(propertyId);
        return nextSet;
      });
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        // 401 = sesión expirada; 403 = password temporal pendiente
        // (password_change_required). Re-sync del estado auth para que la
        // UI reaccione (login o modal de cambio forzado).
        await refreshUser();
      }
    }
  }, [user, watchIds, refreshWatchlist, refreshUser]);

  const setNote = useCallback(async (propertyId: string, note: string) => {
    if (!user) return;
    if (watchIds.has(propertyId)) {
      await api.watchlistUpdateNote(propertyId, note);
    } else {
      await api.watchlistAdd(propertyId, note);
    }
    await refreshWatchlist();
  }, [user, watchIds, refreshWatchlist]);

  const value = useMemo<AuthContextValue>(() => ({
    user, usage, loading, legal, acceptTerms, login, logout, changePassword, completeToken, refreshUser,
    watchIds, watchItems, isWatching, toggleWatch, setNote, refreshWatchlist,
  }), [user, usage, loading, legal, acceptTerms, login, logout, changePassword, completeToken, refreshUser,
      watchIds, watchItems, isWatching, toggleWatch, setNote, refreshWatchlist]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
